import { ChangeEvent, useEffect, useState } from 'react';
import './Recurring.scss';
import Utils from '@/utils/util';
import { ReactComponent as EditIcon } from '@/assets/img/edit-icon-new-v2-red.svg';
import moment from 'moment-timezone';

import { ToggleButton } from '@/components/Elements/ToggleButton';
import { t } from 'i18next';
import { RecurringAppointment, useGetRecurringAppointments } from '../api/getRecurringSlots';
import { CalendarUsers, RecurringDetails } from '@/features/Calendar/types';
import { showNotification } from '@/utils/notificationUtils';
import { Loader } from '@/components/Loader/Loader';
import { Button } from '@/components/Elements';
import { RecurringCard } from './RecurringCard';
import { useGetAvailabilities } from '../api/getAvailability';
import { useAuth } from '@/lib/auth';
import { Service } from '@/features/services';
import { useGetAvailabilitiesForRecurring } from '../api/getAvailabilityRecurring';

type RecurringProps = {
  selectedSlot?: string;
  selectedService?: Service;
  selectedServiceProvider?: CalendarUsers;
  selectedDuration: number;
  onChange: (recurringDetails: RecurringDetails) => void;
  recurringApptmtDetails?: RecurringAppointment[];
  isEditAppointment?: boolean;
  isLoading?: boolean;
};

export const Recurring = ({
  selectedDuration,
  selectedSlot,
  selectedServiceProvider,
  isEditAppointment = false,
  recurringApptmtDetails,
  isLoading = false,
  selectedService,
  onChange,
}: RecurringProps) => {
  const { user } = useAuth();
  const useGetAvailabilitiesMutation = useGetAvailabilitiesForRecurring();
  const getRecurringSlotsMutation = useGetRecurringAppointments();
  const [isRecurringAppointment, setIsRecurringAppointment] = useState(false);
  const [appointmentRecurringType, setAppointmentRecurringType] = useState<string>('weekly');
  const [appointmentRecurringCount, setAppointmentRecurringCount] = useState<number>(10);
  const [tempCount, setTempCount] = useState<number>(10);
  const [editRecurringCount, setEditRecurringCount] = useState<any>(false);
  const [recurringApptmts, setRecurringApptmts] = useState<RecurringAppointment[]>([]);
  const [editDateIndex, setEditDateIndex] = useState<number>(-1);
  useEffect(() => {
    if (recurringApptmtDetails && recurringApptmtDetails.length > 0) {
      setRecurringApptmts(recurringApptmtDetails);
    } else {
      setRecurringApptmts([]);
    }
  }, [recurringApptmtDetails]);
  useEffect(() => {
    if (isEditAppointment) {
      return;
    }
    if (!selectedSlot || !selectedServiceProvider || !selectedDuration) {
      setRecurringApptmts([]);
      setIsRecurringAppointment(false);
      setAppointmentRecurringType('weekly');
      setAppointmentRecurringCount(10);
      setTempCount(10);
      onChange({
        isRecurring: false,
        recurringSlots: [],
        recurringCount: 0,
        recurringFrequency: 'weekly',
      });
      return;
    }
    if (isRecurringAppointment && appointmentRecurringType && appointmentRecurringCount > 0) {
      getRecurringAppointments();
    } else {
      setRecurringApptmts([]);
      if (!isRecurringAppointment) {
        setAppointmentRecurringType('weekly');
        setAppointmentRecurringCount(10);
        setTempCount(10);
      }
    }
  }, [
    isRecurringAppointment,
    appointmentRecurringType,
    appointmentRecurringCount,
    selectedDuration,
    selectedSlot,
    selectedServiceProvider,
  ]);
  // useEffect(() => {
  //   onChange({
  //     isRecurring: isRecurringAppointment,
  //     recurringSlots: recurringApptmts,
  //     recurringCount: appointmentRecurringCount,
  //     recurringFrequency: appointmentRecurringType,
  //   });
  // }, [recurringApptmts]);
  const getRecurringAppointments = async () => {
    if (appointmentRecurringType && isRecurringAppointment && appointmentRecurringCount > 0) {
      if (selectedServiceProvider?.id && selectedSlot) {
        const recurringSlotRes = await getRecurringSlotsMutation.mutateAsync({
          businessUserId: selectedServiceProvider?.id,
          startTime: selectedSlot,
          duration: selectedDuration,
          recurringFrequency: appointmentRecurringType,
          recurringCount: appointmentRecurringCount.toString(),
        });
        setRecurringApptmts(recurringSlotRes);
        onChange({
          isRecurring: isRecurringAppointment,
          recurringSlots: recurringSlotRes,
          recurringCount: appointmentRecurringCount,
          recurringFrequency: appointmentRecurringType,
        });
      }
    }
  };
  const handleChangeRecurringDetails = (event: ChangeEvent<HTMLInputElement> | any) => {
    const value = event.target.value ? event.target.value : '';
    setTempCount(value);
  };
  const onRecurringTypeChange = (value: any) => {
    setAppointmentRecurringType(value);
  };
  const showRecurringSettings = () => {
    if (!isRecurringAppointment) {
      let errorMessage = '';
      if (!selectedServiceProvider) {
        errorMessage = 'Please select a service provider';
      } else if (!selectedSlot) {
        errorMessage = 'Please select a slot';
      } else if (appointmentRecurringCount <= 0) {
        errorMessage = 'Please enter a valid count';
      }
      if (errorMessage) {
        showNotification({
          type: 'info',
          title: errorMessage,
        });
        return;
      }
    }
    setIsRecurringAppointment(!isRecurringAppointment);
    setTimeout(() => {
      Utils.scrollElementToView('recurring-text');
    }, 300);
  };
  const onDeleteClicked = (item: RecurringAppointment, index: number) => {
    const updatedRecurringSlots = [...recurringApptmts];
    updatedRecurringSlots[index].isDeleted = true;
    setRecurringApptmts(updatedRecurringSlots);
  };

  const onRecurringDataChange = (recurringData: RecurringAppointment, index: number) => {
    const newRecurringapptms = [...recurringApptmts];
    newRecurringapptms[index] = recurringData;
    setRecurringApptmts(newRecurringapptms);
    console.log('recurringData change', recurringData, index);

    onChange({
      isRecurring: isRecurringAppointment,
      recurringSlots: newRecurringapptms,
      recurringCount: appointmentRecurringCount,
      recurringFrequency: appointmentRecurringType,
    });
  };

  const getAvailableSlots = async (selectedDate: string, rowIndex: number) => {
    setEditDateIndex(rowIndex);
    const res = await useGetAvailabilitiesMutation.mutateAsync({
      businessId: user?.businessId,
      duration: selectedDuration,
      date: Utils.toServerTz(selectedDate, user?.activeLocation?.timezone),
      tz: moment.tz.guess(true),
      userId: Number(selectedServiceProvider?.id),
      serviceId: Number(selectedService?.id),
      forRecurring: true,
    });
    const newRecurringSlot = res[0];
    newRecurringSlot.id = recurringApptmts[rowIndex].id;
    const updatedRecurringSlots = [...recurringApptmts];
    updatedRecurringSlots.splice(rowIndex, 1, res[0]);
    setEditDateIndex(-1);
    setRecurringApptmts(updatedRecurringSlots);
    onChange({
      isRecurring: isRecurringAppointment,
      recurringSlots: updatedRecurringSlots,
      recurringCount: appointmentRecurringCount,
      recurringFrequency: appointmentRecurringType,
    });
  };

  return (
    <div className="recurring-appointments-container mb-50">
      {!isEditAppointment && (
        <div className="recurring-appointments">
          <div className="settings-item">
            <div className="left-section">
              <h5>{t('Recurring appointment?')}</h5>
              <p>{t('Enable to repeat this appointment on a weekly or monthly basis')}</p>
            </div>
            <div className="right-section">
              {' '}
              <ToggleButton
                isChecked={isRecurringAppointment}
                onChange={showRecurringSettings}
                enableTwoWayBinding
              />
            </div>
          </div>
        </div>
      )}
      {(isRecurringAppointment || isEditAppointment) && (
        <>
          {' '}
          {!isEditAppointment && (
            <>
              <div className="pet-row">
                <div className="pet-col-4 petsked-form">
                  <div className="add-new-title">How often to repeat?</div>

                  <div className="p-radio-group p-pl-30">
                    <label className="container-1">
                      <span>Weekly</span>
                      <input
                        type="radio"
                        name="radio"
                        onChange={() => onRecurringTypeChange('weekly')}
                        checked={appointmentRecurringType === 'weekly'}
                      />
                      <span className="checkmark"></span>
                    </label>
                    <div className="pet-details-divider"></div>
                    <label className="container-1">
                      <span>Monthly</span>
                      <input
                        type="radio"
                        name="radio"
                        onChange={() => onRecurringTypeChange('monthly')}
                        checked={appointmentRecurringType === 'monthly'}
                      />
                      <span className="checkmark"></span>
                    </label>
                  </div>
                </div>
              </div>
              <div>
                <div className="recurring-appointments">
                  <div className="add-new-title">Repeated dates &amp; times</div>
                  <div className="selection">
                    <p className="recurring-text">
                      Your appointment is set to recur for the next{' '}
                      <span className="edit-recurring">
                        {appointmentRecurringCount}{' '}
                        {appointmentRecurringType === 'monthly' ? 'months' : 'weeks'}{' '}
                        <EditIcon
                          className="ml-3"
                          onClick={() => setEditRecurringCount(!editRecurringCount)}
                        />
                      </span>
                    </p>
                  </div>
                </div>
                {editRecurringCount && (
                  <div className="pet-row mtop-2 mt-3">
                    <div className="action-row pet-col-4 petsked-form">
                      <input
                        type="number"
                        className="new-cust-input"
                        placeholder="Enter Recurring Count"
                        id="recurring-count"
                        onChange={handleChangeRecurringDetails}
                        value={tempCount}
                        maxLength={2}
                        max={10}
                      />
                      <Button
                        variant="inverse"
                        size="lg"
                        onClick={() => {
                          if (tempCount > 12) {
                            showNotification({
                              type: 'info',
                              title: 'You can only select a maximum of 12',
                            });
                            return;
                          }
                          if (tempCount <= 1) {
                            showNotification({
                              type: 'info',
                              title: 'Minimum of 2 recurring appointments required',
                            });
                            return;
                          }
                          setAppointmentRecurringCount(tempCount);
                          setEditRecurringCount(!editRecurringCount);
                        }}
                      >
                        {t('Save')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
          <div className="recurring-slots">
            {getRecurringSlotsMutation.isLoading || isLoading ? (
              <Loader text="" />
            ) : (
              recurringApptmts?.map((item: any, index: number) => {
                return item.isDeleted ? (
                  <></>
                ) : (
                  <RecurringCard  
                    onDeleteClicked={onDeleteClicked}
                    itemIndex={index}
                    recurringData={item}
                    onChange={onRecurringDataChange}
                    disableDelete={isEditAppointment}
                    onEditDate={(date: string) => {
                      getAvailableSlots(date, index);
                    }}
                    isLoading={
                      index === editDateIndex ? useGetAvailabilitiesMutation.isLoading : false
                    }
                  />
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
};
